import { useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import { units } from '../units'
import type { PantryItem } from '../types'

const PAGE_SIZE = 25

export function PantryTableView({ items }: { items: PantryItem[] }) {
  const [page, setPage] = useState(0)

  const sorted = [...items].sort((a, b) => {
    const n = a.ingredient_name.localeCompare(b.ingredient_name)
    if (n !== 0) return n
    return (a.expires_at ?? '9999').localeCompare(b.expires_at ?? '9999')
  })

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const pageItems = sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  if (sorted.length === 0) {
    return (
      <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted, padding: '12px 0' }}>
        No pantry lots to show.
      </div>
    )
  }

  return (
    <div>
      <div style={{
        overflowX: 'auto',
        background: theme.surface, border: theme.cardBorder, borderRadius: theme.radiusLg,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: theme.sans, fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${theme.lineStrong}` }}>
              {['Item', 'Qty', 'Unit', 'Purchased', 'Expires', 'Notes', ''].map((h, i) => (
                <th key={i} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageItems.map(item => (
              <PantryTableRow key={item.id} item={item} />
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: 12, padding: '10px 4px 0',
          fontFamily: theme.sans, fontSize: 13, color: theme.inkSoft,
        }}>
          <span>{safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}</span>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={safePage === 0}
            style={pageBtn(safePage === 0)}
          >‹ Prev</button>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
            style={pageBtn(safePage >= totalPages - 1)}
          >Next ›</button>
        </div>
      )}
    </div>
  )
}

function PantryTableRow({ item }: { item: PantryItem }) {
  const [error, setError] = useState<string | null>(null)

  const save = async (patch: Parameters<typeof api.updatePantryItem>[1]) => {
    setError(null)
    try {
      await api.updatePantryItem(item.id, patch)
    } catch (e) {
      setError(String(e))
    }
  }

  const handleDelete = async () => {
    setError(null)
    try {
      await api.deletePantryItem(item.id)
    } catch (e) {
      setError(String(e))
    }
  }

  const daysToExpiry = item.expires_at
    ? Math.floor((new Date(item.expires_at).getTime() - Date.now()) / 86400000)
    : null

  return (
    <>
      <tr style={{ borderBottom: `1px solid ${theme.line}` }}>
        <td style={{ ...tdStyle, minWidth: 140, color: theme.ink }}>
          {item.ingredient_name}
          {item.recipe_id && (
            <span style={recipeBadgeStyle}>recipe</span>
          )}
        </td>
        <EditableNumberCell
          value={item.quantity}
          onSave={v => save({ quantity: v })}
        />
        <EditableUnitCell
          value={item.unit ?? 'ea'}
          locked={!!item.recipe_id}
          onSave={v => save({ unit: v })}
        />
        <EditableDateCell
          value={item.purchased_at ?? ''}
          onSave={v => save({ purchased_at: v })}
        />
        <EditableDateCell
          value={item.expires_at ?? ''}
          onSave={v => save({ expires_at: v })}
          warn={daysToExpiry !== null && daysToExpiry <= 3}
        />
        <EditableTextCell
          value={item.notes ?? ''}
          onSave={v => save({ notes: v })}
        />
        <td style={{ ...tdStyle, width: 32, textAlign: 'center' }}>
          <button
            onClick={handleDelete}
            title="Remove lot"
            style={{ all: 'unset', cursor: 'pointer', color: theme.muted, fontSize: 18, lineHeight: 1 }}
          >×</button>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={7} style={{
            padding: '3px 10px 6px',
            fontFamily: theme.sans, fontSize: 11,
            color: theme.accentInk,
          }}>
            {error}
          </td>
        </tr>
      )}
    </>
  )
}

function EditableNumberCell({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const start = () => { setDraft(String(value)); setEditing(true) }
  const commit = () => {
    setEditing(false)
    const n = parseFloat(draft)
    if (!isNaN(n) && n > 0 && n !== value) onSave(n)
  }

  return (
    <td style={{ ...tdStyle, minWidth: 60 }} onClick={!editing ? start : undefined}>
      {editing ? (
        <input
          autoFocus type="number" min="0" step="any"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }}
          style={{ ...inputStyle, width: 70 }}
        />
      ) : (
        <span style={{ cursor: 'pointer', color: theme.ink }}>{value}</span>
      )}
    </td>
  )
}

function EditableUnitCell({ value, locked, onSave }: {
  value: string; locked: boolean; onSave: (v: string) => void
}) {
  if (locked) {
    return <td style={{ ...tdStyle, minWidth: 80, color: theme.muted }}>{value}</td>
  }
  return (
    <td style={{ ...tdStyle, minWidth: 80 }}>
      <select
        value={value}
        onChange={e => onSave(e.target.value)}
        style={{
          fontFamily: theme.sans, fontSize: 13, color: theme.ink, cursor: 'pointer',
          border: 'none', background: 'transparent', padding: 0, outline: 'none',
        }}
      >
        {units.map(u => (
          <option key={u.code} value={u.code}>{u.label}</option>
        ))}
      </select>
    </td>
  )
}

function EditableDateCell({ value, onSave, warn }: {
  value: string; onSave: (v: string) => void; warn?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const start = () => { setDraft(value); setEditing(true) }
  const commit = () => {
    setEditing(false)
    if (draft !== value) onSave(draft)
  }

  return (
    <td style={{ ...tdStyle, minWidth: 120 }} onClick={!editing ? start : undefined}>
      {editing ? (
        <input
          autoFocus type="date"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }}
          style={inputStyle}
        />
      ) : (
        <span style={{
          cursor: 'pointer',
          color: value ? (warn ? 'oklch(0.42 0.16 28)' : theme.ink) : theme.muted,
        }}>
          {value || '—'}
        </span>
      )}
    </td>
  )
}

function EditableTextCell({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const start = () => { setDraft(value); setEditing(true) }
  const commit = () => {
    setEditing(false)
    if (draft !== value) onSave(draft)
  }

  return (
    <td style={{ ...tdStyle, minWidth: 120 }} onClick={!editing ? start : undefined}>
      {editing ? (
        <input
          autoFocus type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }}
          style={{ ...inputStyle, minWidth: 100 }}
        />
      ) : (
        <span style={{ cursor: 'pointer', color: value ? theme.ink : theme.muted }}>
          {value || '—'}
        </span>
      )}
    </td>
  )
}

const thStyle: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left',
  fontFamily: theme.sans, fontSize: 10, letterSpacing: 1, fontWeight: 700,
  textTransform: 'uppercase', color: theme.muted, whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '8px 10px', verticalAlign: 'middle',
}

const inputStyle: React.CSSProperties = {
  fontFamily: theme.sans, fontSize: 13, color: theme.ink,
  background: theme.bg, border: `1px solid ${theme.accent}`,
  borderRadius: 4, padding: '2px 6px', boxSizing: 'border-box',
  outline: 'none',
}

const pageBtn = (disabled: boolean): React.CSSProperties => ({
  all: 'unset', cursor: disabled ? 'default' : 'pointer',
  fontFamily: theme.sans, fontSize: 13,
  color: disabled ? theme.muted : theme.accent,
  padding: '2px 4px',
})

const recipeBadgeStyle: React.CSSProperties = {
  marginLeft: 6, fontSize: 9, letterSpacing: 1, textTransform: 'uppercase',
  color: theme.accent, fontWeight: 700,
  border: `1px solid ${theme.accent}`, borderRadius: 4, padding: '1px 4px',
}
